import { useState } from "react";
import { Alert, Badge, Button, Card, Col, Row, Spinner } from "react-bootstrap";
import type { ISpreadsheetTemplateActive } from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { isMappingValidationResult } from "./mappingValidation";
import { ReplaceTemplateModal } from "./ReplaceTemplateModal";
import { formatFileSize } from "./wasteReturnTemplateMappingDefaults";

export interface TemplateUploadPanelProps {
    activeTemplate: ISpreadsheetTemplateActive | null;
    loading: boolean;
    uploading: boolean;
    uploadError: string | null;
    activeError: string | null;
    onUpload: (file: File, name: string) => Promise<boolean>;
    onClearUploadError: () => void;
}

export function TemplateUploadPanel({
    activeTemplate,
    loading,
    uploading,
    uploadError,
    activeError,
    onUpload,
    onClearUploadError,
}: TemplateUploadPanelProps) {
    const [showReplaceModal, setShowReplaceModal] = useState(false);

    const handleOpenModal = () => {
        onClearUploadError();
        setShowReplaceModal(true);
    };

    const handleCloseModal = () => {
        if (uploading) return;
        onClearUploadError();
        setShowReplaceModal(false);
    };

    if (loading) {
        return (
            <Card className="mb-4">
                <Card.Body className="d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    <span>Loading active template…</span>
                </Card.Body>
            </Card>
        );
    }

    return (
        <>
            <Card className="mb-4">
                <Card.Header>Active Template</Card.Header>
                <Card.Body>
                    {activeError && <Alert variant="danger">{activeError}</Alert>}

                    {!activeTemplate ? (
                        <Alert variant="info" className="mb-3">
                            No active EA Waste Return template has been uploaded yet. Upload an official `.xlsx` workbook to
                            begin.
                        </Alert>
                    ) : (
                        <Row className="g-3 mb-3">
                            <Col md={6}>
                                <div className="text-muted small">File name</div>
                                <div>{activeTemplate.originalFileName}</div>
                            </Col>
                            <Col md={6}>
                                <div className="text-muted small">Admin label</div>
                                <div>{activeTemplate.name || "—"}</div>
                            </Col>
                            <Col md={6}>
                                <div className="text-muted small">File size</div>
                                <div>{formatFileSize(activeTemplate.fileSizeBytes)}</div>
                            </Col>
                            <Col md={6}>
                                <div className="text-muted small">File ID</div>
                                <div className="font-monospace small text-break">{activeTemplate.fileHashSha256}</div>
                            </Col>
                            <Col md={6}>
                                <div className="text-muted small">Last updated (UTC)</div>
                                <div>{new Date(activeTemplate.updatedAtUtc).toLocaleString()}</div>
                            </Col>
                            <Col md={3}>
                                <div className="text-muted small">Mapped</div>
                                <Badge bg={activeTemplate.hasMapping ? "success" : "secondary"}>
                                    {activeTemplate.hasMapping ? "Yes" : "No"}
                                </Badge>
                            </Col>
                            <Col md={3}>
                                <div className="text-muted small">Validation</div>
                                {isMappingValidationResult(activeTemplate.validationSummary) ? (
                                    <Badge bg={activeTemplate.validationSummary.isValid ? "success" : "warning"}>
                                        {activeTemplate.validationSummary.isValid ? "Valid" : "Issues"}
                                    </Badge>
                                ) : (
                                    <Badge bg="secondary">Not run</Badge>
                                )}
                            </Col>
                        </Row>
                    )}

                    <Button
                        variant={activeTemplate ? "outline-secondary" : "primary"}
                        onClick={handleOpenModal}
                        disabled={uploading}
                    >
                        {activeTemplate ? "Replace template" : "Upload template"}
                    </Button>
                </Card.Body>
            </Card>

            <ReplaceTemplateModal
                show={showReplaceModal}
                hasActiveTemplate={!!activeTemplate}
                uploading={uploading}
                uploadError={uploadError}
                onHide={handleCloseModal}
                onUpload={onUpload}
            />
        </>
    );
}
